import { useEffect, useRef, useState } from "react";
import { useIsMountedRef } from "./useIsMounted";

/**
 * Handles loading, error and data states, async multiple request instances finishes and provides refetch function
 * @param {function(...any): Promise<T>} asyncFunction - The asynchronous function to be called
 * @param {any[]} params - Parameters to be passed to the asynchronous function.¨
 * @param {boolean} shouldFetchOnMount - Whether the fetch should be fired immediately
 * @returns {[T|undefined, boolean, Error|null, function(...any): Promise<T>]} An array containing data values, loading state, error, and refetch function
 */
export function useFetch(asyncFunction, params, shouldFetchOnMount = true) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(undefined);
  const isMounted = useIsMountedRef();
  const latestFetchId = useRef(0);

  const fetchData = async (...fetchParams) => {
    setIsLoading(true);
    setError(null);

    const currentFetchId = Date.now();
    latestFetchId.current = currentFetchId;

    try {
      const result = await asyncFunction(
        ...(fetchParams.length > 0 ? fetchParams : params)
      );
      if (isMounted && currentFetchId === latestFetchId.current) {
        setData(result.data);
      }
    } catch (error) {
      if (isMounted && currentFetchId === latestFetchId.current) {
        setError(error);
        console.error(error);
      }
    } finally {
      if (isMounted && currentFetchId === latestFetchId.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (shouldFetchOnMount && !isLoading) {
      setIsLoading(true);
      fetchData();
    }
  }, [shouldFetchOnMount]);

  return [data, isLoading, error, fetchData];
}
